import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import type { Timestamp } from 'firebase/firestore';
import { AlertTriangle } from 'lucide-react';
import { db } from '../lib/firebase';
import { isIosShell } from '../lib/nativeBridge';

/** Organisations from before plans existed: same date as GRANDFATHER_TRIAL_END on the server. */
const GRANDFATHER_TRIAL_END = new Date('2026-11-07T23:59:59+08:00');

interface LabPlan {
  active?: boolean;
  comped?: boolean;
  trialEndsAt?: Timestamp;
  paidUntil?: Timestamp;
}

/**
 * A line across the top when the organisation's plan needs attention: the
 * trial ending within a week (owners and admins), or the organisation being
 * read-only (everyone, so nobody wonders why saving fails).
 *
 * In the iPhone app there is no link to pay, only who to ask (guideline 3.1.1).
 */
export default function PlanBanner({
  code,
  orgRole,
}: {
  code: string | null;
  orgRole: 'owner' | 'admin' | 'member' | null;
}) {
  const [lab, setLab] = useState<LabPlan | null>(null);
  useEffect(() => {
    if (!code) {
      setLab(null);
      return undefined;
    }
    return onSnapshot(
      doc(db, 'labs', code),
      (s) => setLab((s.data() as LabPlan | undefined) ?? null),
      () => setLab(null),
    );
  }, [code]);

  if (!lab || lab.comped) return null;
  const now = Date.now();
  const trialEnd = lab.trialEndsAt?.toDate() ?? GRANDFATHER_TRIAL_END;
  const paid = lab.paidUntil ? lab.paidUntil.toDate().getTime() > now : false;
  const manager = orgRole === 'owner' || orgRole === 'admin';
  const canPay = manager && !isIosShell();
  const action = canPay ? (
    <Link to="/org" className="font-semibold underline">
      Subscribe
    </Link>
  ) : manager ? null : (
    <span>Ask your organisation’s owner to subscribe.</span>
  );

  let tone = '';
  let text = '';
  if (lab.active === false) {
    tone = 'border-red-800 bg-red-950 text-red-200';
    text = 'Your organisation is suspended, so BlisxBench is read-only. Contact BlisxBench support.';
  } else if (!paid && trialEnd.getTime() <= now) {
    tone = 'border-amber-800 bg-amber-950 text-amber-200';
    text = `Your organisation’s ${lab.paidUntil ? 'subscription' : 'free trial'} has ended, so BlisxBench is read-only. Everything can still be viewed and exported.`;
  } else if (!paid && manager) {
    const days = Math.ceil((trialEnd.getTime() - now) / 86_400_000);
    if (days > 7) return null;
    tone = 'border-sky-800 bg-sky-950 text-sky-200';
    text = `Your free trial ends in ${days} day${days === 1 ? '' : 's'}.`;
  } else {
    return null;
  }

  return (
    <div className={`border-b ${tone}`}>
      <p className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-2 gap-y-1 px-4 py-2 text-sm">
        <AlertTriangle size={15} className="shrink-0" />
        <span>{text}</span>
        {lab.active !== false && action}
      </p>
    </div>
  );
}
